import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  Loader2, Users, UserMinus, AlertTriangle,
  Mail, MailOpen, MousePointerClick, Forward, ChevronLeft,
  ChevronRight, CalendarDays, BarChart2, X, Download,
} from 'lucide-react';
import { interspireApi } from '../services/api';
import { SendStats, SendRecipientRow } from '../types';
import DarkModeToggle from '../components/DarkModeToggle';

const PAGE_LIMIT = 50;

function formatDate(ts: number | null): string {
  if (ts == null || ts === 0) return '—';
  return new Date(ts * 1000).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true,
  });
}

function fmtNum(n: number | null | undefined): string {
  if (n == null) return '—';
  return n.toLocaleString();
}

function pct(part: number | null, total: number): string {
  if (part == null || total === 0) return '';
  return ` (${((part / total) * 100).toFixed(1)}%)`;
}

function ratePct(part: number | null, total: number): string {
  if (part == null || total === 0) return '0.0%';
  return `${((part / total) * 100).toFixed(1)}%`;
}

// ── CSV Export ────────────────────────────────────────────────────────────────

function exportToCsv(rows: SendRecipientRow[], filename: string) {
  const header = ['Email', 'First Name', 'Last Name'];
  const lines  = [
    header.join(','),
    ...rows.map(r => [
      `"${(r.email    || '').replace(/"/g, '""')}"`,
      `"${(r.firstName|| '').replace(/"/g, '""')}"`,
      `"${(r.lastName || '').replace(/"/g, '""')}"`,
    ].join(',')),
  ];
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// ── SVG Donut Chart ──────────────────────────────────────────────────────────

interface DonutSegment {
  value: number;
  color: string;
  label: string;
}

function DonutChart({ segments, total }: { segments: DonutSegment[]; total: number }) {
  const r = 52;
  const cx = 64;
  const cy = 64;
  const circ = 2 * Math.PI * r;

  const nonZero = segments.filter(s => s.value > 0 && total > 0);

  if (nonZero.length === 0 || total === 0) {
    return (
      <div className="flex items-center justify-center w-32 h-32 rounded-full bg-gray-100 dark:bg-slate-700 text-gray-400 text-xs">
        No data
      </div>
    );
  }

  let cumPct = 0;
  const arcs = nonZero.map(seg => {
    const proportion = seg.value / total;
    const dashLen = proportion * circ;
    const offset = cumPct * circ;
    cumPct += proportion;
    return { ...seg, dashLen, offset };
  });

  return (
    <svg viewBox="0 0 128 128" width={128} height={128} className="shrink-0">
      {arcs.map((arc, i) => (
        <circle
          key={i}
          r={r}
          cx={cx}
          cy={cy}
          fill="none"
          stroke={arc.color}
          strokeWidth={20}
          strokeDasharray={`${arc.dashLen} ${circ}`}
          strokeDashoffset={-arc.offset}
          transform={`rotate(-90 ${cx} ${cy})`}
          style={{ transition: 'stroke-dashoffset 0.3s' }}
        />
      ))}
      <text x={cx} y={cy - 6} textAnchor="middle" fontSize="11" fontWeight="bold" fill="currentColor" className="text-gray-800 dark:text-white">
        {total.toLocaleString()}
      </text>
      <text x={cx} y={cy + 8} textAnchor="middle" fontSize="8" fill="#9ca3af">
        recipients
      </text>
    </svg>
  );
}

// ── Stat Card ────────────────────────────────────────────────────────────────

interface StatCardProps {
  label: string;
  value: string | number | null;
  icon: React.ReactNode;
  sub?: string;
  color?: string;
  unavailable?: boolean;
}

function StatCard({ label, value, icon, sub, color = 'text-gray-700 dark:text-gray-200', unavailable }: StatCardProps) {
  return (
    <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl p-4 flex flex-col gap-1 shadow-sm">
      <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 font-medium uppercase tracking-wide">
        {icon}
        {label}
      </div>
      <div className={`text-2xl font-bold tabular-nums ${unavailable ? 'text-gray-300 dark:text-slate-600' : color}`}>
        {unavailable ? '—' : (value == null ? '—' : typeof value === 'number' ? value.toLocaleString() : value)}
      </div>
      {sub && !unavailable && (
        <div className="text-xs text-gray-400 dark:text-slate-500">{sub}</div>
      )}
      {unavailable && (
        <div className="text-xs text-gray-300 dark:text-slate-600">not tracked</div>
      )}
    </div>
  );
}

// ── Recipient Table ──────────────────────────────────────────────────────────

type ListType = 'all' | 'bounced' | 'unsubscribed';

interface RecipientTableProps {
  statid: number;
  type: ListType;
  label: string;
  campaignName: string;
}

function RecipientTable({ statid, type, label, campaignName }: RecipientTableProps) {
  const [rows, setRows]             = useState<SendRecipientRow[]>([]);
  const [total, setTotal]           = useState(0);
  const [page, setPage]             = useState(1);
  const [loading, setLoading]       = useState(false);
  const [exporting, setExporting]   = useState(false);
  const [error, setError]           = useState<string | null>(null);

  const load = useCallback(async (p: number) => {
    setLoading(true);
    setError(null);
    try {
      const res = await interspireApi.getSendRecipients(statid, type, p, PAGE_LIMIT);
      setRows(res.data.rows);
      setTotal(res.data.total);
      setPage(p);
    } catch (err: any) {
      setError(err?.response?.data?.error ?? err?.message ?? 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [statid, type]);

  useEffect(() => { load(1); }, [load]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await interspireApi.getSendRecipientsExport(statid, type);
      const slug = type === 'all' ? 'contacts' : type === 'bounced' ? 'bounced' : 'unsubscribed';
      const name = campaignName.replace(/[^a-z0-9]/gi, '_').toLowerCase().slice(0, 40);
      exportToCsv(res.data.rows, `send_${statid}_${slug}_${name}.csv`);
    } catch (err: any) {
      setError(err?.response?.data?.error ?? err?.message ?? 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_LIMIT));

  return (
    <div className="mt-3">
      {error && (
        <div className="text-sm text-red-600 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg px-3 py-2 mb-2">
          {error}
        </div>
      )}
      {loading ? (
        <div className="flex items-center gap-2 py-8 justify-center text-gray-400">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span className="text-sm">Loading {label.toLowerCase()}…</span>
        </div>
      ) : rows.length === 0 ? (
        <div className="text-sm text-gray-400 text-center py-8">No contacts found.</div>
      ) : (
        <>
          {/* Export button */}
          <div className="flex justify-end mb-2">
            <button
              onClick={handleExport}
              disabled={exporting}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-60"
            >
              {exporting
                ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                : <Download className="h-3.5 w-3.5" />
              }
              {exporting ? 'Exporting…' : `Export CSV (${total.toLocaleString()})`}
            </button>
          </div>

          <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-slate-700">
            <table className="min-w-full text-sm divide-y divide-gray-100 dark:divide-slate-700">
              <thead className="bg-gray-50 dark:bg-slate-800">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Email</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">First Name</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Last Name</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50 dark:divide-slate-700/50">
                {rows.map((r, i) => (
                  <tr key={i} className="hover:bg-gray-50 dark:hover:bg-slate-700/40 transition-colors">
                    <td className="px-4 py-2 text-gray-700 dark:text-gray-300 font-mono text-xs">{r.email || '—'}</td>
                    <td className="px-4 py-2 text-gray-600 dark:text-gray-400">{r.firstName || '—'}</td>
                    <td className="px-4 py-2 text-gray-600 dark:text-gray-400">{r.lastName || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between mt-2 px-1">
            <span className="text-xs text-gray-400">
              {total.toLocaleString()} contact{total !== 1 ? 's' : ''} total
            </span>
            {totalPages > 1 && (
              <div className="flex items-center gap-1.5">
                <button
                  onClick={() => load(page - 1)}
                  disabled={page === 1 || loading}
                  className="p-1 rounded text-gray-500 hover:bg-gray-200 dark:hover:bg-slate-700 disabled:opacity-30 transition-colors"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <span className="text-xs text-gray-500 tabular-nums">
                  Page {page} of {totalPages}
                </span>
                <button
                  onClick={() => load(page + 1)}
                  disabled={page === totalPages || loading}
                  className="p-1 rounded text-gray-500 hover:bg-gray-200 dark:hover:bg-slate-700 disabled:opacity-30 transition-colors"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ── Main Page ────────────────────────────────────────────────────────────────

export default function SendStatsPage() {
  const { statid: statidParam } = useParams<{ statid: string }>();
  const statid = parseInt(statidParam ?? '', 10);

  const [stats, setStats]     = useState<SendStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);

  const [activeList, setActiveList] = useState<ListType | null>(null);

  useEffect(() => {
    if (isNaN(statid)) {
      setError('Invalid send ID');
      setLoading(false);
      return;
    }
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await interspireApi.getSendStats(statid);
        setStats(res.data);
      } catch (err: any) {
        setError(err?.response?.data?.error ?? err?.message ?? 'Failed to load statistics');
      } finally {
        setLoading(false);
      }
    })();
  }, [statid]);

  const toggleList = (t: ListType) => setActiveList(prev => prev === t ? null : t);

  const handleClose = () => window.close();

  // ── Loading state ─────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-slate-900 flex flex-col">
        <StandaloneHeader campaignName="" loading />
        <div className="flex items-center justify-center flex-1 gap-2 text-gray-400">
          <Loader2 className="h-6 w-6 animate-spin" />
          <span>Loading statistics…</span>
        </div>
        <DarkModeToggle />
      </div>
    );
  }

  // ── Error state ───────────────────────────────────────────────────────────
  if (error || !stats) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-slate-900 flex flex-col">
        <StandaloneHeader campaignName="" onClose={handleClose} />
        <div className="max-w-3xl mx-auto w-full px-6 py-8">
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg px-4 py-3 text-sm text-red-700 dark:text-red-400">
            {error ?? 'Statistics not found.'}
          </div>
        </div>
        <DarkModeToggle />
      </div>
    );
  }

  const total = stats.recipientsCount;

  // Chart segments
  const hasOpenData = stats.opened != null;
  const chartSegments = hasOpenData
    ? [
        { value: stats.opened ?? 0,      color: '#3b82f6', label: 'Opened' },
        { value: stats.unopened ?? 0,    color: '#93c5fd', label: 'Unopened' },
        { value: stats.totalBounces,     color: '#f87171', label: 'Bounced' },
        { value: stats.unsubscribes,     color: '#fbbf24', label: 'Unsubscribed' },
      ]
    : [
        { value: total - stats.totalBounces - stats.unsubscribes, color: '#3b82f6', label: 'Delivered' },
        { value: stats.totalBounces,  color: '#f87171', label: 'Bounced' },
        { value: stats.unsubscribes,  color: '#fbbf24', label: 'Unsubscribed' },
      ];

  const legendItems = chartSegments.filter(s => s.value > 0);

  const drillButtons: { type: ListType; label: string; count: number | null; icon: React.ReactNode; color: string }[] = [
    { type: 'all',          label: 'Contacts Sent To',  count: total,               icon: <Users className="h-4 w-4" />,         color: 'bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/20 dark:hover:bg-blue-800/30 dark:text-blue-300 dark:border-blue-700' },
    { type: 'bounced',      label: 'Bounced Addresses', count: stats.totalBounces,  icon: <AlertTriangle className="h-4 w-4" />, color: 'bg-red-50 hover:bg-red-100 text-red-700 border-red-200 dark:bg-red-900/20 dark:hover:bg-red-800/30 dark:text-red-300 dark:border-red-700' },
    { type: 'unsubscribed', label: 'Unsubscribers',     count: stats.unsubscribes, icon: <UserMinus className="h-4 w-4" />,     color: 'bg-amber-50 hover:bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:hover:bg-amber-800/30 dark:text-amber-300 dark:border-amber-700' },
  ];

  const listLabels: Record<ListType, string> = {
    all:          'Contacts Sent To',
    bounced:      'Bounced Addresses',
    unsubscribed: 'Unsubscribers from This Send',
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-900 flex flex-col">

      {/* Standalone header — no app nav */}
      <StandaloneHeader campaignName={stats.campaignName} onClose={handleClose} />

      {/* Page body */}
      <div className="w-[90%] mx-auto px-4 py-6 flex-1 space-y-6">

        {/* Campaign meta + dates */}
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            {/* Campaign name — 2× prominent */}
            <h1 className="text-4xl font-extrabold text-gray-900 dark:text-white leading-tight">
              {stats.campaignName}
            </h1>
            <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-gray-500 dark:text-gray-400">
              <span>Send ID: <span className="font-mono font-semibold text-gray-700 dark:text-gray-200">{stats.statid}</span></span>
              {stats.listName && <span>List: <span className="font-semibold text-gray-700 dark:text-gray-200">{stats.listName}</span></span>}
              {stats.segmentName && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-100 dark:bg-blue-900/20 dark:text-blue-300 dark:border-blue-700">
                  {stats.segmentName}
                </span>
              )}
            </div>
          </div>

          {/* Send dates */}
          <div className="flex flex-col gap-1.5 text-sm bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl px-4 py-3 shadow-sm">
            <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
              <CalendarDays className="h-4 w-4 text-gray-400 dark:text-gray-500 shrink-0" />
              <span className="text-gray-500 dark:text-gray-400 text-xs w-20">Started</span>
              <span className="font-medium tabular-nums">{formatDate(stats.startDate)}</span>
            </div>
            <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
              <CalendarDays className="h-4 w-4 text-gray-400 dark:text-gray-500 shrink-0" />
              <span className="text-gray-500 dark:text-gray-400 text-xs w-20">Finished</span>
              <span className="font-medium tabular-nums">{formatDate(stats.endDate)}</span>
            </div>
          </div>
        </div>

        {/* Stats cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
          <StatCard
            label="Recipients"
            value={stats.recipientsCount}
            icon={<Users className="h-3.5 w-3.5" />}
            color="text-blue-600 dark:text-blue-400"
          />
          <StatCard
            label="Unsubscribe Rate"
            value={ratePct(stats.unsubscribes, total)}
            icon={<UserMinus className="h-3.5 w-3.5" />}
            sub={`${fmtNum(stats.unsubscribes)} unsubscribes`}
            color="text-amber-600 dark:text-amber-400"
          />
          <StatCard
            label="Bounce Rate"
            value={ratePct(stats.totalBounces, total)}
            icon={<AlertTriangle className="h-3.5 w-3.5" />}
            sub={`${fmtNum(stats.totalBounces)} bounced · Soft: ${fmtNum(stats.bounceSoft)} · Hard: ${fmtNum(stats.bounceHard)}`}
            color="text-red-600 dark:text-red-400"
          />
          <StatCard
            label="Opened"
            value={stats.opened}
            icon={<MailOpen className="h-3.5 w-3.5" />}
            sub={stats.opened != null ? pct(stats.opened, total) : undefined}
            color="text-green-600 dark:text-green-400"
            unavailable={stats.opened == null}
          />
          <StatCard
            label="Unopened"
            value={stats.unopened}
            icon={<Mail className="h-3.5 w-3.5" />}
            sub={stats.unopened != null ? pct(stats.unopened, total) : undefined}
            color="text-slate-600 dark:text-slate-400"
            unavailable={stats.unopened == null}
          />
          {stats.clicked != null && (
            <StatCard
              label="Link Clicks"
              value={stats.clicked}
              icon={<MousePointerClick className="h-3.5 w-3.5" />}
              sub={pct(stats.clicked, total)}
              color="text-purple-600 dark:text-purple-400"
            />
          )}
          {stats.forwarded != null && (
            <StatCard
              label="Forwarded"
              value={stats.forwarded}
              icon={<Forward className="h-3.5 w-3.5" />}
              sub={pct(stats.forwarded, total)}
              color="text-indigo-600 dark:text-indigo-400"
            />
          )}
        </div>

        {/* Chart + legend */}
        <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl p-5 shadow-sm">
          <h2 className="text-sm font-semibold text-blue-600 dark:text-blue-400 uppercase tracking-wide mb-4">
            {hasOpenData ? 'Engagement Breakdown' : 'Delivery Breakdown'}
          </h2>
          <div className="flex flex-wrap items-center gap-8">
            <DonutChart segments={chartSegments} total={total} />

            <div className="flex flex-col gap-2.5 flex-1 min-w-[180px]">
              {legendItems.map((seg, i) => {
                const proportion = total > 0 ? seg.value / total : 0;
                return (
                  <div key={i} className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-sm shrink-0" style={{ backgroundColor: seg.color }} />
                    <div className="flex-1">
                      <div className="flex items-center justify-between gap-2 text-sm text-gray-700 dark:text-gray-300">
                        <span>{seg.label}</span>
                        <span className="font-semibold tabular-nums">{seg.value.toLocaleString()}</span>
                      </div>
                      <div className="h-1.5 rounded-full bg-gray-100 dark:bg-slate-700 mt-1 overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all duration-500"
                          style={{ width: `${(proportion * 100).toFixed(1)}%`, backgroundColor: seg.color }}
                        />
                      </div>
                      <div className="text-xs text-gray-400 dark:text-slate-500 mt-0.5">
                        {(proportion * 100).toFixed(1)}% of recipients
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Drill-down */}
        <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl p-5 shadow-sm">
          <h2 className="text-sm font-semibold text-blue-600 dark:text-blue-400 uppercase tracking-wide mb-4">
            Contact Lists
          </h2>

          <div className="flex flex-wrap gap-2 mb-2">
            {drillButtons.map(btn => (
              <button
                key={btn.type}
                onClick={() => toggleList(btn.type)}
                className={`inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium border rounded-lg transition-colors
                  ${activeList === btn.type
                    ? 'ring-2 ring-blue-400 ring-offset-1 dark:ring-offset-slate-800 ' + btn.color
                    : btn.color
                  }`}
              >
                {btn.icon}
                {btn.label}
                {btn.count != null && (
                  <span className="ml-1 text-xs font-mono opacity-75">({btn.count.toLocaleString()})</span>
                )}
              </button>
            ))}
          </div>

          {activeList && (
            <div className="mt-4 border-t border-gray-100 dark:border-slate-700 pt-4">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
                {listLabels[activeList]}
              </h3>
              <RecipientTable
                key={activeList}
                statid={statid}
                type={activeList}
                label={listLabels[activeList]}
                campaignName={stats.campaignName}
              />
            </div>
          )}
        </div>

      </div>

      <DarkModeToggle />
    </div>
  );
}

// ── Standalone Header (no app nav) ───────────────────────────────────────────

interface StandaloneHeaderProps {
  campaignName: string;
  onClose?: () => void;
  loading?: boolean;
}

function StandaloneHeader({ onClose, loading }: StandaloneHeaderProps) {
  return (
    <header className="bg-white dark:bg-slate-800 border-b border-gray-200 dark:border-slate-700 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between">

        {/* Left: Close Report */}
        <button
          onClick={onClose}
          disabled={loading}
          className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-100 transition-colors disabled:opacity-40"
        >
          <X className="h-4 w-4" />
          Close Report
        </button>

        {/* Center: Logo + title */}
        <div className="flex items-center gap-2">
          <div className="h-7 w-7 rounded-md bg-blue-600 flex items-center justify-center shrink-0">
            <BarChart2 className="h-4 w-4 text-white" />
          </div>
          <span className="text-base font-bold text-gray-900 dark:text-white tracking-tight">
            Send Statistics
          </span>
        </div>

        {/* Right: spacer to balance layout */}
        <div className="w-28" />
      </div>
    </header>
  );
}
